import React, { useState } from 'react';
import { Button, Modal, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrash,
    faExclamationTriangle,
    faClock,
    faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { PrazoDto } from 'services/prazoService';
import { useExcluirPrazo } from 'hooks/usePrazo';
import { useToast } from 'providers/ToastProvider';

interface BotaoExcluirPrazoProps {
    prazo: PrazoDto;
    size?: "sm" | "lg";
    variant?: string;
    title?: string;
}

const BotaoExcluirPrazo: React.FC<BotaoExcluirPrazoProps> = ({
    prazo,
    size = "sm",
    variant = "outline-danger",
    title = "Excluir prazo"
}) => {
    const [showModal, setShowModal] = useState(false);
    const { showToast } = useToast();
    const excluirMutation = useExcluirPrazo();

    const handleClick = () => setShowModal(true);
    const handleClose = () => {
        setShowModal(false);
        excluirMutation.reset();
    };

    const handleConfirm = async () => {
        try {
            await excluirMutation.mutateAsync(prazo.id);
            showToast('Prazo excluído com sucesso', 'success');
            handleClose();
        } catch (error) {
            console.error('Erro ao excluir prazo:', error);
        }
    };

    return (
        <>
            <Button
                variant={variant}
                size={size}
                onClick={handleClick}
                title={title}
            >
                <FontAwesomeIcon icon={faTrash} />
            </Button>

            <Modal show={showModal} onHide={handleClose} centered size="lg" className="fade">
                <Modal.Header className="border-0 pb-0">
                    <div className="d-flex justify-content-center w-100">
                        <div className="rounded-circle bg-danger bg-opacity-10 p-3 mb-3">
                            <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                className="text-danger fa-2x"
                            />
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body className="text-center px-4 pt-0">
                    {excluirMutation.isError && (
                        <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle mb-3">
                            <div className="text-danger-emphasis">
                                <strong>Erro:</strong> Não foi possível excluir o prazo.
                                Tente novamente.
                            </div>
                        </Alert>
                    )}

                    <h4 className="mb-3">Confirmar Exclusão</h4>
                    <p className="mb-3">Você está prestes a excluir o prazo:</p>

                    <div className="d-flex align-items-center justify-content-center mb-4">
                        <div className="border rounded-4 py-3 px-4 bg-body">
                            <div className="d-flex align-items-center">
                                <div className="rounded-circle bg-secondary bg-opacity-10 p-2 me-3 shadow-sm">
                                    <FontAwesomeIcon icon={faClock} className="text-secondary fa-2x" />
                                </div>
                                <div className="text-start">
                                    <h5 className="mb-0">{prazo.descricao}</h5>
                                    <div className="d-flex align-items-center text-muted">
                                        <small className="me-2">Parcelas:</small>
                                        <span className="badge bg-secondary">
                                            {prazo.parcela}x
                                        </span>
                                        {prazo.formasPagamentoCount > 0 && (
                                            <>
                                                <small className="ms-3 me-2">Formas de Pagamento:</small>
                                                <span className="badge bg-light text-body-emphasis border">
                                                    {prazo.formasPagamentoCount}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-body border-start border-4 border-warning rounded p-3 mb-3 text-start">
                        <h6 className="d-flex align-items-center mb-2">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-warning" />
                            Informações Importantes
                        </h6>
                        <p className="text-muted small mb-0">
                            Ao excluir este prazo, ele será marcado como inativo e não aparecerá mais nas listagens.
                            Esta ação poderá ser revertida posteriormente pelo administrador através da reativação.
                        </p>
                    </div>
                </Modal.Body>

                <Modal.Footer className="border-0 justify-content-center pt-0">
                    <Button
                        variant="outline-secondary"
                        onClick={handleClose}
                        disabled={excluirMutation.isPending}
                        className="px-4"
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleConfirm}
                        disabled={excluirMutation.isPending}
                        className="px-4"
                    >
                        {excluirMutation.isPending ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Excluindo...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faTrash} className="me-2" />
                                Excluir Prazo
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default BotaoExcluirPrazo;